/* eslint-disable react-refresh/only-export-components */

import { useEffect, useState } from 'react';
import { Paper, Text, Button, Group, Stack, Anchor, CloseButton } from '@mantine/core';
import { Link } from 'react-router-dom';

/**
 * Cookie Consent Banner Component
 *
 * GDPR/RODO Compliance:
 * - Requires explicit consent before loading analytics cookies
 * - Implements Google Consent Mode v2
 * - Stores user preference in localStorage
 * - Valid for 12 months (automatically re-prompts after expiry)
 *
 * Cookie Categories:
 * - Strictly Necessary: Authentication, CSRF protection (NO CONSENT REQUIRED)
 * - Analytics: Google Analytics 4 (CONSENT REQUIRED)
 *
 * User Controls:
 * - Accept All: Enable all cookies including analytics
 * - Reject All (Only Essential): Only necessary cookies
 * - Cookie Settings: Granular control (future enhancement)
 *
 * Privacy Policy: /privacy-policy
 * Cookie Details: See Privacy Policy Section 8
 */

export interface CookieConsentState {
  accepted: boolean;
  date: string;
  analytics: boolean;
}

const CONSENT_STORAGE_KEY = 'cookie-consent';
const CONSENT_VERSION = '1.0'; // Increment when cookie policy changes
const CONSENT_EXPIRY_DAYS = 365; // 12 months

export function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user has already given consent
    const storedConsent = localStorage.getItem(CONSENT_STORAGE_KEY);

    if (!storedConsent) {
      // No consent stored - show banner
      setVisible(true);
      setIsLoading(false);
      return;
    }

    try {
      const consent: CookieConsentState & { version?: string; expiryDate?: string } =
        JSON.parse(storedConsent);

      // Check if consent is expired or version changed
      const consentDate = new Date(consent.date);
      const expiryDate = new Date(consentDate);
      expiryDate.setDate(expiryDate.getDate() + CONSENT_EXPIRY_DAYS);

      const isExpired = new Date() > expiryDate;
      const isOutdated = consent.version !== CONSENT_VERSION;

      if (isExpired || isOutdated) {
        // Consent expired or policy changed - re-prompt user
        localStorage.removeItem(CONSENT_STORAGE_KEY);
        setVisible(true);
        setIsLoading(false);
        return;
      }

      // Valid consent exists - apply preferences
      applyConsentPreferences(consent);
      setVisible(false);
      setIsLoading(false);
    } catch (error) {
      // Invalid consent data - re-prompt
      localStorage.removeItem(CONSENT_STORAGE_KEY);
      setVisible(true);
      setIsLoading(false);
    }
  }, []);

  const handleAcceptAll = () => {
    const consent: CookieConsentState = {
      accepted: true,
      date: new Date().toISOString(),
      analytics: true,
    };

    saveConsent(consent);
    applyConsentPreferences(consent);
    setVisible(false);
  };

  const handleRejectAll = () => {
    const consent: CookieConsentState = {
      accepted: false,
      date: new Date().toISOString(),
      analytics: false,
    };

    saveConsent(consent);
    applyConsentPreferences(consent);
    setVisible(false);
  };

  const saveConsent = (consent: CookieConsentState) => {
    const consentData = {
      ...consent,
      version: CONSENT_VERSION,
    };

    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consentData));
  };

  const applyConsentPreferences = (consent: CookieConsentState) => {
    // Update Google Consent Mode v2
    if (window.gtag) {
      if (consent.analytics) {
        // User accepted analytics - enable GA4
        window.gtag('consent', 'update', {
          analytics_storage: 'granted',
          ad_storage: 'denied', // We don't use ads
          ad_user_data: 'denied',
          ad_personalization: 'denied',
        });
      } else {
        // User rejected analytics - disable GA4
        window.gtag('consent', 'update', {
          analytics_storage: 'denied',
          ad_storage: 'denied',
          ad_user_data: 'denied',
          ad_personalization: 'denied',
        });
      }
    }
  };

  // Don't show anything while checking existing consent
  if (isLoading) {
    return null;
  }

  // Don't show banner if user already decided
  if (!visible) {
    return null;
  }

  return (
    <Paper
      shadow="xl"
      p="lg"
      radius="md"
      style={{
        position: 'fixed',
        bottom: 20,
        left: 20,
        right: 20,
        zIndex: 10000,
        maxWidth: 600,
        margin: '0 auto',
        border: '2px solid var(--mantine-color-blue-6)',
      }}
    >
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <Text fw={700} size="lg" c="blue">
            Używamy plików cookie
          </Text>
          <CloseButton
            onClick={handleRejectAll}
            aria-label="Odrzuć wszystkie opcjonalne cookies"
            title="Odrzuć wszystkie (tylko niezbędne)"
          />
        </Group>

        <Text size="sm" c="dimmed">
          Ta strona używa{' '}
          <Text component="span" fw={600} c="dark">
            niezbędnych plików cookie
          </Text>{' '}
          do prawidłowego działania (uwierzytelnianie, bezpieczeństwo) oraz{' '}
          <Text component="span" fw={600} c="dark">
            opcjonalnych plików analitycznych
          </Text>{' '}
          (Google Analytics) do poprawy jakości usług.
        </Text>

        <Text size="sm" c="dimmed">
          Możesz wybrać, które kategorie plików cookie akceptujesz. Pliki niezbędne są zawsze
          aktywne.
        </Text>

        <Text size="xs" c="dimmed">
          Więcej informacji w{' '}
          <Anchor component={Link} to="/privacy-policy" size="xs" fw={600}>
            Polityce Prywatności (Sekcja 8)
          </Anchor>
        </Text>

        <Group gap="sm" mt="xs">
          <Button onClick={handleAcceptAll} size="sm" fullWidth>
            Akceptuję wszystkie
          </Button>
          <Button onClick={handleRejectAll} variant="outline" size="sm" fullWidth>
            Tylko niezbędne
          </Button>
        </Group>

        <Text size="xs" c="dimmed" ta="center" mt={-8}>
          Akceptując, wyrażasz zgodę zgodnie z RODO na przetwarzanie danych przez Google Analytics
          (USA).
        </Text>
      </Stack>
    </Paper>
  );
}

// Re-export hook and utility function from separate file for Fast Refresh compatibility
export { useCookieConsent, revokeConsent } from './useCookieConsent';
